import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Badge } from "../../components/Badge";
import { buttonClass } from "../../components/Button";
import { api } from "../../api/client";

import type { MeetingNoteRow } from "../../api/types";

// 報價單上的「這個案子的紀錄」(第 18 輪)。
//
// --------------------------------------------------------------------------
// 編輯器裡**不放編輯器**
// --------------------------------------------------------------------------
// 這一塊只列標題與日期,點了才在對話框裡開。
//
// 理由是第 5 輪定下的「安靜編輯」:報價單編輯器是看數字的工具。
// 在裡面塞一個十行的內容框,會讓一個本來只是要改單價的人捲三頁才找得到那一格。
//
// --------------------------------------------------------------------------
// 為什麼不用 react-query
// --------------------------------------------------------------------------
// 編輯器那棵樹的測試沒有 `QueryClientProvider`(`AttachmentPanel`、
// `ProjectCost` 都是為了同一個理由用 `useEffect` + `api`)。

export interface QuoteMeetingNotesProps {
  quoteId: number;
}

export function QuoteMeetingNotes({ quoteId }: QuoteMeetingNotesProps) {
  const [rows, setRows] = useState<MeetingNoteRow[]>([]);

  const refresh = useCallback(() => {
    // 形狀要擋:拿到不是陣列的東西直接 `.map()` 會在 render 當場丟例外,
    // 而 React 會把**整棵樹**卸載 —— 報價單本體跟著消失,
    // 而真正壞掉的只是底下這一小塊。
    api
      .get<MeetingNoteRow[]>(`/quotes/${quoteId}/meeting-notes`)
      .then((got) => setRows(Array.isArray(got) ? got : []))
      .catch(() => undefined);
  }, [quoteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <section className="flex flex-col gap-3" aria-label="這個案子的紀錄">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">這個案子的紀錄</h3>
        {/* **開新分頁,不是懸浮視窗、也不是直接跳走。**
            業主 2026-09-17 說新增不要用懸浮視窗(會議可能很長),那沒錯;
            但從報價單編輯器「跳走」去寫紀錄,**手上還沒存的改動就沒了** ——
            那比一個小視窗糟得多。開新分頁兩邊都保得住:編輯器留在這裡,
            紀錄在另一頁慢慢寫。

            用 `<a>` 不用 `<Link>`:這棵樹會被報價單編輯器整個掛進去,
            而編輯器是一棵**不放 Router 的純元件樹**(十九個測試檔這樣渲染它)。 */}
        <a
          href={`/quote/meetings/new?quote=${quoteId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass("secondary", "sm", "no-underline")}
        >
          <Plus aria-hidden="true" size={14} />
          寫一份
        </a>
      </div>

      {rows.length === 0 ? (
        // **不用 EmptyState** —— 它帶著兔子吉祥物,而第 5 輪定下的「安靜編輯」是
        // 報價單編輯器整頁都不放品牌素材(`tests/brand.test.tsx` 守著)。
        <p className="m-0 text-sm text-[var(--text-3)]">
          這個案子還沒有會議紀錄、結案報告或檢討報告。
        </p>
      ) : (
        <ul className="m-0 flex flex-col gap-1 p-0">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex list-none flex-wrap items-center gap-2 border-t border-[var(--border)] py-1.5 text-sm first:border-t-0"
            >
              <Badge tone="neutral">{row.kind_label}</Badge>
              <a
                href={`/quote/meetings/${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[var(--text)] underline-offset-2 hover:underline"
              >
                {row.title}
              </a>
              <span className="text-[var(--text-3)]">
                {row.met_on} · {row.author_name}
              </span>
            </li>
          ))}
        </ul>
      )}

    </section>
  );
}
